import { useState, useRef, useEffect } from 'react';
import { Lock, KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import client from '../../src/api/client';
import '../styles/admin-base.css';

export default function SudoModeModal({ open, onSuccess, onCancel }) {
  const [method, setMethod] = useState('password'); // 'password' | 'totp'
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open, method]);

  useEffect(() => {
    if (open) {
      setPassword('');
      setTotpCode('');
      setError(null);
      setLoading(false);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const payload = method === 'password'
      ? { password }
      : { totpCode: totpCode.trim() };

    if (method === 'password' && !password) {
      setError('Password is required.');
      setLoading(false);
      return;
    }
    if (method === 'totp' && !totpCode.trim()) {
      setError('Authenticator code is required.');
      setLoading(false);
      return;
    }

    try {
      const response = await client.post('/api/admin/auth/sudo', payload);
      const { sudoToken } = response.data;
      onSuccess(sudoToken);
    } catch (err) {
      setError(err.response?.data?.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div className="admin-sudo-overlay" onClick={handleBackdropClick}>
      <div className="admin-sudo-panel">
        <div className="admin-sudo-header">
          <div className="admin-sudo-icon">
            <ShieldCheck size={24} />
          </div>
          <h3>Confirm Your Identity</h3>
          <p>This action requires re-authentication for security.</p>
        </div>

        {/* Method toggle tabs */}
        <div className="admin-sudo-tabs">
          <button
            type="button"
            className={`admin-sudo-tab ${method === 'password' ? 'is-active' : ''}`}
            onClick={() => {
              setMethod('password');
              setError(null);
            }}
          >
            <Lock size={14} className="admin-icon-gap" />
            Password
          </button>
          <button
            type="button"
            className={`admin-sudo-tab ${method === 'totp' ? 'is-active' : ''}`}
            onClick={() => {
              setMethod('totp');
              setError(null);
            }}
          >
            <KeyRound size={14} className="admin-icon-gap" />
            Authenticator
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="admin-alert is-error">{error}</div>}

          {method === 'password' ? (
            <div className="admin-form-group">
              <label htmlFor="sudo-password">Password</label>
              <div className="admin-input-wrapper">
                <Lock size={18} className="admin-input-icon" />
                <input
                  ref={inputRef}
                  type={showPassword ? 'text' : 'password'}
                  id="sudo-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="admin-input"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="admin-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
          ) : (
            <div className="admin-form-group">
              <label htmlFor="sudo-totp">Authenticator Code</label>
              <div className="admin-totp-input">
                <input
                  ref={inputRef}
                  type="text"
                  id="sudo-totp"
                  value={totpCode}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d{0,6}$/.test(val)) setTotpCode(val);
                  }}
                  placeholder="000000"
                  maxLength={6}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                />
              </div>
            </div>
          )}

          <div className="admin-sudo-actions">
            <button
              type="button"
              className="admin-button is-soft"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="admin-button is-primary"
              disabled={loading || (method === 'password' ? !password : totpCode.length !== 6)}
            >
              {loading ? <Loader2 size={16} className="admin-spin" /> : 'Verify'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
